import React, { useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { fetchJson } from '@atlassian/jira-fetch';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import type {
	DeploymentProvidersInfo,
	Provider,
	DeploymentProvidersInfoProviderInputs,
} from './types';
import { isBitbucketSCMProvider, isBitbucketPipelineProvider, isBitbucketProvider } from './utils';

export const useDevOpsProviders = (): ServiceQueryResultWithFetch<Provider[]> => {
	const baseUrl = '';

	const request = useCallback(
		() => fetchJson(`${baseUrl}/rest/dev-status/1.0/provider/capabilities`),
		[baseUrl],
	);

	const { loading, error, data, fetch } = useService(request);

	useEffect(() => {
		fetch();
	}, [fetch]);

	return {
		loading,
		error,
		data,

		fetch,
	};
};

const useMemoByProviderId = (value: Provider[]): Provider[] => {
	const ref = useRef<Provider[]>(value);

	if (
		ref.current.length !== value.length ||
		!ref.current.every((x, i) => x.providerId === value[i].providerId)
	) {
		ref.current = value;
	}

	return ref.current;
};

// All tenants now have the "bitbucket pipelines" provider installed
// Thus we must check if the tenant has the "bitbucket" provider. If they do then they can send deployments.
export const filterOutBitbucketIfNotConnected = (devOpsProviders: Provider[]): Provider[] => {
	const isBitbucketConnectedToJira = devOpsProviders.some(isBitbucketSCMProvider);
	const isBitbucketPipelineFound = devOpsProviders.some(isBitbucketPipelineProvider);

	// All tenants should have the bitbucket pipeline provider. Only return it if they have the "bitbucket" provider - indicating they have set up the bitbucket integration
	if (!isBitbucketConnectedToJira && isBitbucketPipelineFound) {
		return devOpsProviders.filter((x: Provider) => !isBitbucketProvider(x));
	}

	return devOpsProviders;
};

const useMemoizedDeploymentProviders = (
	devOpsProviderResult: ServiceQueryResultWithFetch<Provider[]>,
) => {
	const data: Provider[] = devOpsProviderResult.data
		? filterOutBitbucketIfNotConnected(devOpsProviderResult.data).filter((x: Provider) =>
				x.capabilities.includes('DEPLOYMENT'),
			)
		: [];
	return useMemoByProviderId(data);
};

export const useDeploymentProviders = (): ServiceQueryResultWithFetch<Provider[]> => {
	const devOpsProviderResult = useDevOpsProviders();
	const memoizedProviders = useMemoizedDeploymentProviders(devOpsProviderResult);

	return devOpsProviderResult.data
		? {
				...devOpsProviderResult,
				data: memoizedProviders,
			}
		: devOpsProviderResult;
};

export const useDeploymentProvidersInfo =
	(): ServiceQueryResultWithFetch<DeploymentProvidersInfo> => {
		const devOpsProviderResult = useDevOpsProviders();
		const memoizedProviders = useMemoizedDeploymentProviders(devOpsProviderResult);

		return devOpsProviderResult.data
			? {
					...devOpsProviderResult,
					data: {
						deploymentProviders: memoizedProviders,
						isBitbucketDvcsConnectedToJira: devOpsProviderResult.data.some(isBitbucketSCMProvider),
					},
				}
			: {
					...devOpsProviderResult,
					data: undefined,
				};
	};
// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
export const DeploymentProvidersInfoContext = createContext<
	ServiceQueryResultWithFetch<DeploymentProvidersInfo> | undefined
>();

export const DeploymentProvidersInfoProvider = ({
	children,
}: DeploymentProvidersInfoProviderInputs) => {
	const deploymentProvidersState = useDeploymentProvidersInfo();
	return (
		<DeploymentProvidersInfoContext.Provider value={deploymentProvidersState}>
			{children}
		</DeploymentProvidersInfoContext.Provider>
	);
};

export const useDeploymentProvidersInfoContext = () => {
	const result = useContext(DeploymentProvidersInfoContext);
	if (result === undefined) {
		throw new Error('Missing service provider');
	}

	return result;
};

export const useVCSProviders = (): ServiceQueryResultWithFetch<Provider[]> => {
	const devOpsProviderResult = useDevOpsProviders();

	const data = devOpsProviderResult.data
		? devOpsProviderResult.data.filter((x) => x.capabilities.includes('COMMIT'))
		: [];
	const memoizedData = useMemoByProviderId(data);

	return devOpsProviderResult.data
		? {
				...devOpsProviderResult,
				data: memoizedData,
			}
		: devOpsProviderResult;
};
